/**
 * /owner/membership/dashboard — KPI tiles + revenue + by-tier breakdown.
 */
import { useQuery } from '@tanstack/react-query';
import { Crown, Users, UserCheck, UserX, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { PageHeader, SectionCard, EmptyState, FullSpinner, QueryError } from '@/components/ui_primitives/Misc';
import KpiCard from '@/components/ui_primitives/KpiCard';
import { rupiah } from '@/lib/membershipFmt';

export default function OwnerMembershipDashboardPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);

  const dashQ = useQuery({
    queryKey: qk.ownerMembershipDashboard(brandId),
    queryFn: () => api.ownerMembershipDashboard(token, brandId),
    enabled: !!token && !!brandId,
  });

  const tiersQ = useQuery({
    queryKey: qk.ownerMembershipTiers(brandId),
    queryFn: () => api.ownerMembershipTiers(token, brandId),
    enabled: !!token && !!brandId,
  });

  if (dashQ.isLoading) return <FullSpinner />;
  if (dashQ.isError) return <QueryError error={dashQ.error} onRetry={() => dashQ.refetch()} />;
  const d = dashQ.data;

  // Backend's dashboard returns every tier in `members_by_tier`, including
  // archived/test fixtures. We trim to currently-active tiers (or any tier
  // with non-zero members so historical assignments stay visible) on the
  // client to keep the UI clean. Source of truth remains the backend.
  const activeTierIds = new Set(
    (tiersQ.data ?? []).filter((t) => t.active).map((t) => t.id),
  );
  const tierRows = (d.members_by_tier ?? [])
    .filter((t) => activeTierIds.has(t.tier_id) || (t.count || 0) > 0)
    .sort((a, b) => (b.count || 0) - (a.count || 0));

  return (
    <div data-testid="owner-membership-dashboard">
      <PageHeader
        kicker="Memberships"
        title="Dashboard"
        subtitle="Snapshot of paid memberships across your brand. Numbers refresh on every assign or cancel."
      />

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          testID="kpi-total"
          tone="gold" icon={<Users size={14} />}
          label="Total Members" value={d.total_members}
          sub="All-time"
        />
        <KpiCard
          testID="kpi-active"
          tone="green" icon={<UserCheck size={14} />}
          label="Active" value={d.active_members}
          sub="Currently entitled"
        />
        <KpiCard
          testID="kpi-expired"
          tone="slate" icon={<UserX size={14} />}
          label="Expired" value={d.expired_members}
          sub="Past expiry date"
        />
        <KpiCard
          testID="kpi-cancelled"
          tone="red" icon={<UserX size={14} />}
          label="Cancelled" value={d.cancelled_members}
          sub="Manually ended"
        />
      </div>

      {/* Revenue + by-tier */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard testID="dashboard-revenue-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold">
              <Wallet size={16} />
            </div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-white/55">
              Membership Revenue
            </div>
          </div>
          <div
            className="font-display text-4xl font-bold tracking-tight mt-2"
            data-testid="dashboard-revenue-value"
          >
            {rupiah(d.membership_revenue)}
          </div>
          <div className="text-xs text-white/45 mt-1.5">
            Lifetime sum of price_paid (excludes cancelled).
          </div>
        </SectionCard>

        <SectionCard testID="dashboard-by-tier-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-gold/15 border border-gold/30 grid place-items-center text-gold">
              <Crown size={16} />
            </div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-white/55">
              Members by Tier
            </div>
          </div>
          {tierRows.length === 0 ? (
            <EmptyState
              title="No active members yet"
              subtitle="Tier counts will appear here as you assign memberships."
              testID="dashboard-by-tier-empty"
            />
          ) : (
            <ul className="space-y-2" data-testid="dashboard-by-tier-list">
              {tierRows.map((t) => (
                <li
                  key={t.tier_id}
                  data-testid={`dashboard-tier-row-${t.tier_id}`}
                  className="flex items-center justify-between rounded-xl bg-black/30 border border-white/8 px-3 py-2.5"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{
                        background: t.color || '#C9A84C',
                        boxShadow: `0 0 8px ${t.color || '#C9A84C'}66`,
                      }}
                    />
                    <span className="text-sm font-medium truncate">{t.tier_name}</span>
                  </div>
                  <span className="text-sm font-mono font-semibold text-gold">
                    {t.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
